import { signOut, useSession } from "next-auth/react";
import { Link as ChakraLink } from "@chakra-ui/react";
import NextLink from "next/link";
import {
  Building2,
  Home,
  LogOut,
  Receipt,
  Settings,
  Wrench,
  BarChart,
  TrendingUp,
} from "lucide-react";
import styles from "./sidebar.module.css";
import { useSelector } from "react-redux";
import { selectCurrentOrganization } from "@/stores/organizations-store/organizations-selectors";
import { UserOrgRole } from "@prisma/client";
import { useRouter } from "next/router";

export default function Sidebar() {
  const { data: session, status } = useSession();
  const isLoading = status === "loading";
  const isAuthenticated = Boolean(session);
  const organization = useSelector(selectCurrentOrganization);
  const isTenant = organization?.userOrgRole === UserOrgRole.TENANT;
  const router = useRouter();
  const currentPath = router.pathname;

  // Helper to determine if a link is active
  const isActive = (href: string) => {
    if (href === "/dashboard" && currentPath === "/") return true;
    if (href === "/dashboard" && currentPath.startsWith("/dashboard"))
      return true;
    if (href === "/properties" && currentPath.startsWith("/properties"))
      return true;
    if (href === "/transactions" && currentPath.startsWith("/transactions"))
      return true;
    if (href === "/reports" && currentPath.startsWith("/reports")) return true;
    if (href === "/cash-flow" && currentPath.startsWith("/cash-flow"))
      return true;
    if (href === "/maintenance" && currentPath.startsWith("/maintenance"))
      return true;
    if (href === "/org-settings" && currentPath.startsWith("/org-settings"))
      return true;
    if (href === "/rental-history" && currentPath.startsWith("/rental-history"))
      return true;
    return currentPath === href;
  };

  // Always render the sidebar container to preserve width
  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarContainer}>
        {isLoading ? (
          // Optionally, render a skeleton or keep empty for now
          <div style={{ height: "100%" }} />
        ) : isAuthenticated ? (
          <>
            {!isTenant && (
              <>
                <ChakraLink
                  as={NextLink}
                  href='/dashboard'
                  className={
                    isActive("/dashboard")
                      ? `${styles.navLink} ${styles.activeNavLink}`
                      : styles.navLink
                  }
                >
                  <Home className={styles.icon} />
                  <span>Dashboard</span>
                </ChakraLink>
                <ChakraLink
                  as={NextLink}
                  href='/properties'
                  className={
                    isActive("/properties")
                      ? `${styles.navLink} ${styles.activeNavLink}`
                      : styles.navLink
                  }
                >
                  <Building2 className={styles.icon} />
                  <span>Properties</span>
                </ChakraLink>
                <ChakraLink
                  as={NextLink}
                  href='/transactions'
                  className={
                    isActive("/transactions")
                      ? `${styles.navLink} ${styles.activeNavLink}`
                      : styles.navLink
                  }
                >
                  <Receipt className={styles.icon} />
                  <span>Transactions</span>
                </ChakraLink>
                <ChakraLink
                  as={NextLink}
                  href='/cash-flow'
                  className={
                    isActive("/cash-flow")
                      ? `${styles.navLink} ${styles.activeNavLink}`
                      : styles.navLink
                  }
                >
                  <TrendingUp className={styles.icon} />
                  <span>Cash Flow</span>
                </ChakraLink>
                <ChakraLink
                  as={NextLink}
                  href='/reports'
                  className={
                    isActive("/reports")
                      ? `${styles.navLink} ${styles.activeNavLink}`
                      : styles.navLink
                  }
                >
                  <BarChart className={styles.icon} />
                  <span>Reports</span>
                </ChakraLink>
              </>
            )}
            {isTenant && (
              <ChakraLink
                as={NextLink}
                href='/rental-history'
                className={
                  isActive("/rental-history")
                    ? `${styles.navLink} ${styles.activeNavLink}`
                    : styles.navLink
                }
              >
                <Home className={styles.icon} />
                <span>Rental History</span>
              </ChakraLink>
            )}
            <ChakraLink
              as={NextLink}
              href='/maintenance'
              className={
                isActive("/maintenance")
                  ? `${styles.navLink} ${styles.activeNavLink}`
                  : styles.navLink
              }
            >
              <Wrench className={styles.icon} />
              <span>Maintenance</span>
            </ChakraLink>
            <ChakraLink
              as={NextLink}
              href='/org-settings'
              className={
                isActive("/org-settings")
                  ? `${styles.navLink} ${styles.activeNavLink}`
                  : styles.navLink
              }
            >
              <Settings className={styles.icon} />
              <span>Org Settings</span>
            </ChakraLink>
            <ChakraLink
              as='button'
              onClick={() => signOut({ callbackUrl: "/", redirect: true })}
              className={styles.navLink}
            >
              <LogOut className={styles.icon} />
              <span>Logout</span>
            </ChakraLink>
          </>
        ) : (
          // Not authenticated: keep width, show nothing or a placeholder
          <div style={{ height: "100%" }} />
        )}
      </div>
    </div>
  );
}
